import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Camera, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { HISTORY } from "@/data/history";
import { HISTORY_TR } from "@/data/i18n/content";
import { MaskLines } from "@/components/MaskLines";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { locById, useLang } from "@/lib/i18n";

const NavBtn = ({ dir, onClick }) => {
  const { t } = useLang();
  return (
  <button type="button" onClick={onClick} className={`hs-lb-nav ${dir === "prev" ? "left-2" : "right-2"}`} aria-label={t(dir === "prev" ? "photo.prev" : "photo.next")} data-testid={`history-lightbox-${dir}`}>
    {dir === "prev" ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
  </button>
  );
};

const Lightbox = ({ photos: PHOTOS, index, setIndex }) => {
  const entry = index === null ? null : PHOTOS[index];
  const go = (d) => setIndex((i) => (i + d + PHOTOS.length) % PHOTOS.length);
  const onKey = (ev) => {
    if (ev.key === "ArrowRight") go(1);
    if (ev.key === "ArrowLeft") go(-1);
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) setIndex(null); }}>
      <DialogContent onKeyDown={onKey} className="max-w-[min(94vw,1100px)] border-white/10 bg-[#0d0c0a] p-3 text-[#efe8dc] sm:p-4" data-testid="history-lightbox">
        <DialogTitle className="sr-only">{entry?.title}</DialogTitle>
        {entry && (
          <figure className="relative m-0">
            <img key={entry.id} src={entry.photo} alt={entry.title} className="hs-lb-img mx-auto max-h-[78vh] w-auto max-w-full rounded-lg object-contain" data-testid="history-lightbox-img" />
            <NavBtn dir="prev" onClick={() => go(-1)} />
            <NavBtn dir="next" onClick={() => go(1)} />
            <figcaption className="mono mt-3 flex justify-between gap-4 text-[11px] uppercase tracking-[0.18em] text-[#b3aa9c]">
              <span data-testid="history-lightbox-caption">{entry.date} · {entry.title}</span>
              <span data-testid="history-lightbox-count">{index + 1} / {PHOTOS.length}</span>
            </figcaption>
          </figure>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Card = ({ e, i, onOpen }) => {
  const { t } = useLang();
  return (
  <article className={`hs-card ${i % 2 ? "hs-card-low" : ""}`} data-testid={`history-card-${e.id}`}>
    <span className="hs-dot" />
    <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--green)]">{e.tag}</p>
    <p className="font-display mt-2 text-4xl leading-none md:text-5xl">{e.date}</p>
    <h3 className="font-display mt-4 text-xl md:text-2xl">{e.title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]">{e.body}</p>
    {e.photo ? (
      <button type="button" className="hs-photo-btn" onClick={() => onOpen(e.id)} aria-label={`${t("photo.zoom")}: ${e.title}`} data-testid={`history-photo-open-${e.id}`}>
        <img src={e.photo} alt={e.title} className="hs-photo" loading="lazy" data-testid={`history-photo-${e.id}`} style={{ objectFit: e.fit ?? "cover", objectPosition: e.pos ?? "center" }} />
        <span className="hs-photo-zoom"><Expand size={14} /></span>
      </button>
    ) : (
      <div className="hs-photo hs-photo-empty" data-testid={`history-photo-slot-${e.id}`}>
        <Camera size={18} />
        <span className="mono text-[11px]">[{t("photo.slot")} {e.date}]</span>
      </div>
    )}
    {e.link && (
      <a href={e.link} className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--green)]" data-testid={`history-link-${e.id}`}>
        {t("story.seeProject")} <ArrowUpRight size={15} />
      </a>
    )}
  </article>
  );
};

const EndPanel = () => {
  const { t } = useLang();
  return (
  <div className="hs-end" data-testid="history-end">
    <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("story.goesOn")}</p>
    <p className="font-display mt-3 text-3xl leading-[1.05] md:text-5xl">{t("story.end1")} <em>{t("story.end2")}</em></p>
    <a href="#proyecto" className="btn-primary mt-8" data-testid="history-to-project-btn">
      {t("story.meet")} <ArrowRight size={16} />
    </a>
  </div>
  );
};

const useTrackDistance = (trackRef) => {
  const [dist, setDist] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (el) setDist(Math.max(0, el.scrollWidth - window.innerWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [trackRef]);
  return dist;
};

export const HistorySection = () => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const dist = useTrackDistance(trackRef);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.3 });
  const x = useTransform(smooth, (v) => -v * dist);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(null);
  const { t, lang } = useLang();
  const items = useMemo(() => HISTORY.map((e) => locById(e, HISTORY_TR, lang)), [lang]);
  const photos = useMemo(() => items.filter((e) => e.photo), [items]);
  const openById = (id) => setOpen(photos.findIndex((p) => p.id === id));
  useMotionValueEvent(scrollYProgress, "change", (v) => setIdx(Math.min(HISTORY.length - 1, Math.floor(v * HISTORY.length))));

  return (
    <section id="mi-historia" className="pt-24" data-testid="history-page">
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("story.kicker")}</p>
        <MaskLines as="h2" className="font-display mt-4 text-4xl leading-[0.95] sm:text-5xl lg:text-6xl" lines={[t("story.l1"), <em key="e">{t("story.l2")}</em>]} />
        <p className="mt-6 max-w-2xl text-sm text-[var(--muted)] md:text-base">
          {t("story.intro")}
        </p>
      </div>
      <section ref={wrapRef} className="relative" style={{ height: `calc(${dist}px + 100vh)` }} data-testid="history-scroller">
        <div className="sticky top-0 flex h-screen flex-col overflow-hidden pt-[72px]">
          <div className="flex items-end justify-between px-5 pt-3 md:px-10">
            <p className="font-display text-3xl text-[var(--green)] md:text-4xl" data-testid="history-current-date">{items[idx].date}</p>
            <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]" data-testid="history-counter">
              {String(idx + 1).padStart(2, "0")} / {HISTORY.length}
            </p>
          </div>
          <div className="relative mx-5 mt-4 h-px bg-white/15 md:mx-10">
            <motion.div className="absolute inset-0 origin-left bg-[var(--green)]" style={{ scaleX: smooth }} />
          </div>
          <div className="relative flex flex-1 items-start pt-12 md:pt-14">
            <div className="hs-rail" />
            <motion.div ref={trackRef} style={{ x }} className="hs-track" data-testid="history-track">
              {items.map((e, i) => <Card key={e.id} e={e} i={i} onOpen={openById} />)}
              <EndPanel />
            </motion.div>
          </div>
        </div>
      </section>
      <Lightbox photos={photos} index={open} setIndex={setOpen} />
    </section>
  );
};
